import React, { useEffect, useState, useMemo, useCallback } from "react";

import styles from "./BeverageLandingPages.module.css";
import { useBeveragesV2 } from "../../hooks/useBeveragesV2";
import { useAcid } from "../../hooks/useAcid";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import useToast from "../../components/Toast/useToast";
import { sendPageViewWhenConsentReady } from "../../utils/adobeDataLayerUtils";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import { CharacterCount } from "~/components";

export const BeverageV2 = (props) => {
  const { isThankYou } = props;
  const navigate = useNavigate();
  useAcid(); // Persist acid from URL into sessionStorage for BeverageV2Save
  const { getBeveragesAi, clearCurrent } = useBeveragesV2();
  const { showWarningToast } = useToast();
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    clearCurrent();
  }, []);

  useEffect(() => {
    return sendPageViewWhenConsentReady(
      isThankYou ? "Beverage V2 Thank You" : "Beverage V2 Landing",
      'Passions-FavBevs',
      'beverages2',
    );
  }, [isThankYou]);

  const onDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const isDisabled = useMemo(() => {
    return !description?.trim() || loading;
  }, [loading, description]);

  const submit = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getBeveragesAi(description);
      const hasResult = result.hasSelection && !result.hasAQuestion;
      if (hasResult) {
        navigate("/beverage2/confirm");
      } else {
        showWarningToast(
          result.question ??
            "Unfortunately, we couldn't identify any beverage preferences from your input. Please try to explain in other words",
          30_000,
        );
      }
    } catch (error) {
      console.log("Error getting beverage preferences", error);
    } finally {
      setLoading(false);
    }
  }, [description, getBeveragesAi, navigate]);

  return (
    <>
      <main className={styles.main}>
        <figure className={styles.figure}>
          <img
            className={styles.image}
            src="https://cache.marriott.com/is/image/marriotts7prod/bonvoy-points-redemption--33160?wid=1440"
            alt="Drinks"
          />

          <figcaption className={styles.caption}>
            <h2 className={classNames(styles.title, styles.sharingTitle)}>
              {isThankYou ? (
                "Thanks for sharing!"
              ) : (
                <>
                  What's your go-to drink? <br /> We'd love to know your
                  favorites.
                </>
              )}
            </h2>

            <h2 className={classNames(styles.title, styles.secondTitle)}>
              {isThankYou ? (
                "Tell us about another favorite drink?"
              ) : (
                <>What are you sipping on?</>
              )}
            </h2>

            <textarea
              disabled={loading}
              onChange={onDescriptionChange}
              className={classNames(
                styles.textarea,
                isThankYou ? styles.thankYouArea : null,
              )}
              placeholder="Share what you enjoy and when you enjoy it"
              maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
            ></textarea>
            <CharacterCount
              value={description}
              max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
            />

            {!isThankYou ? (
              <p className={styles.text}>
                Coffee to kickstart or a cocktail to unwind – what drink(s)
                would make your day?
              </p>
            ) : null}

            {isThankYou ? (
              <div className={classNames(styles.buttons, styles.buttonsInline)}>
                {/* <a
                  className={styles.secondaryButton}
                  href="https://www.marriott.com/default.mi"
                >
                  No, thanks
                </a> */}

                <button
                  onClick={submit}
                  disabled={isDisabled}
                  className={`${styles.submit} ${styles.fullWidth} custom_click_track`}
                  data-custom_click_track_value="BeverageV2|Done|internal"
                >
                  {loading ? <span className={styles.loader}></span> : null}
                  {loading ? "Analyzing" : isDisabled ? "Share" : "Done"}
                </button>
              </div>
            ) : (
              <button
                onClick={submit}
                disabled={isDisabled}
                className={`${styles.submit} custom_click_track`}
                data-custom_click_track_value="BeverageV2|Done|internal"
              >
                {loading ? <span className={styles.loader}></span> : null}
                {loading ? "Analyzing" : "Get Started"}
              </button>
            )}
          </figcaption>
        </figure>

        {isThankYou ? (
          <footer className={classNames(styles.footer)}>
            We need your permission for the use of your preferences by the
            Marriott Group to deliver personalized services at properties.
          </footer>
        ) : null}
      </main>
    </>
  );
};
